import React, { useMemo, useState } from 'react';
import ChecklistDevicePicker, { type ChecklistPickerDeviceRow } from './ChecklistDevicePicker';
import FieldDeviceRecordDetail, { recordDetailFromLog, type FieldDeviceLineResult } from './FieldDeviceRecordDetail';
import { formatDeviceTypeLabel } from '../../pages/admin/devices/deviceTypes';
import { deviceTypesForFieldSection, matchingDevicesForFieldSection, overallResultFromLines } from '../../lib/fieldDeviceSections';
import { placementForFieldSection } from '../../lib/devicePlacement';
import { formatDevicePlacementLabel } from '../../pages/admin/devices/deviceTypes';
import { apiRequest } from '../../api/client';

type PopulatedDeviceRef = {
    _id?: string;
    type?: string;
    installationLocation?: string;
    floor?: string;
    zone?: string;
    serialNumber?: string;
};

export type FieldDeviceRecord = {
    _id?: string;
    sectionKey: string;
    sectionTitle?: string;
    deviceId?: string | PopulatedDeviceRef | null;
    deviceSnapshot?: {
        type?: string;
        installationLocation?: string;
        floor?: string;
        zone?: string;
        serialNumber?: string;
    };
    overallResult: 'pass' | 'fail' | 'n/a';
    note?: string;
    completedAt?: string;
    lineResults?: FieldDeviceLineResult[];
};

type InspectionPayload = {
    _id: string;
    fieldDeviceRecords?: FieldDeviceRecord[];
};

type SectionItem = {
    itemId: string;
    label: string;
};

type Props = {
    inspectionId: string;
    sectionKey: string;
    sectionTitle: string;
    sectionItems: SectionItem[];
    devices: ChecklistPickerDeviceRow[];
    records: FieldDeviceRecord[];
    disabledReason?: string | null;
    onInspectionUpdated: (row: InspectionPayload) => void;
    onError: (message: string) => void;
};

function deviceIdHex(record: FieldDeviceRecord): string {
    const raw = record.deviceId;
    if (!raw) return '';
    if (typeof raw === 'string') return raw;
    return String(raw._id || '');
}

function recordLocation(record: FieldDeviceRecord): string {
    const snap = record.deviceSnapshot;
    const dev = typeof record.deviceId === 'object' ? record.deviceId : null;
    const loc = snap?.installationLocation || dev?.installationLocation || '';
    const floor = snap?.floor || dev?.floor || '';
    const zone = snap?.zone || dev?.zone || '';
    return [loc, floor, zone].filter(Boolean).join(' · ') || '—';
}

function recordTypeLabel(record: FieldDeviceRecord): string {
    const snap = record.deviceSnapshot;
    const dev = typeof record.deviceId === 'object' ? record.deviceId : null;
    return formatDeviceTypeLabel(snap?.type || dev?.type);
}

function recordKey(record: FieldDeviceRecord): string {
    return record._id || `${record.sectionKey}-${deviceIdHex(record)}`;
}

export default function FieldDeviceSectionPanel(props: Props): React.JSX.Element {
    const {
        inspectionId,
        sectionKey,
        sectionTitle,
        sectionItems,
        devices,
        records,
        disabledReason,
        onInspectionUpdated,
        onError
    } = props;

    const inspectableItems = useMemo(
        () => sectionItems.filter((i) => i.itemId !== 'NEW-BUILDING-NOTICE'),
        [sectionItems]
    );
    const inspectableLabels = useMemo(() => inspectableItems.map((i) => i.label), [inspectableItems]);
    const allowedTypes = useMemo(
        () => deviceTypesForFieldSection(sectionTitle, inspectableLabels),
        [sectionTitle, inspectableLabels]
    );
    const sectionPlacement = useMemo(() => placementForFieldSection(sectionTitle), [sectionTitle]);
    const allowedTypesLabel = useMemo(
        () => (allowedTypes.length > 0 ? allowedTypes.map((t) => formatDeviceTypeLabel(t)).join(', ') : 'none'),
        [allowedTypes]
    );
    const sectionRecords = useMemo(
        () => records.filter((r) => r.sectionKey === sectionKey),
        [records, sectionKey]
    );
    const loggedDeviceIds = useMemo(() => sectionRecords.map(deviceIdHex).filter(Boolean), [sectionRecords]);

    const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
    const [expandedRecordKey, setExpandedRecordKey] = useState<string | null>(null);
    const [lineResults, setLineResults] = useState<Record<string, 'pass' | 'fail' | 'n/a' | null>>({});
    const [summaryNote, setSummaryNote] = useState('');
    const [saving, setSaving] = useState(false);

    const requiredDevices = useMemo(
        () => matchingDevicesForFieldSection(sectionTitle, inspectableLabels, devices),
        [sectionTitle, inspectableLabels, devices]
    );

    const pendingDevices = useMemo(() => {
        return requiredDevices.filter((d) => !loggedDeviceIds.includes(d._id));
    }, [requiredDevices, loggedDeviceIds]);

    const allLinesAnswered = inspectableItems.length > 0 && inspectableItems.every((i) => lineResults[i.itemId]);
    const computedOverall = overallResultFromLines(
        inspectableItems.map((i) => ({ result: lineResults[i.itemId] }))
    );

    async function saveDeviceRecord() {
        if (!selectedDeviceId || !allLinesAnswered) return;
        setSaving(true);
        try {
            const res = await apiRequest<{ success: boolean; data: InspectionPayload }>(
                `/api/inspections/${inspectionId}/field-device-records`,
                {
                    method: 'PATCH',
                    json: {
                        action: 'add',
                        record: {
                            sectionKey,
                            sectionTitle,
                            deviceId: selectedDeviceId,
                            note: summaryNote.trim(),
                            lineResults: inspectableItems.map((item) => ({
                                itemId: item.itemId,
                                label: item.label,
                                result: lineResults[item.itemId] as 'pass' | 'fail' | 'n/a',
                                note: summaryNote.trim()
                            }))
                        }
                    }
                }
            );
            onInspectionUpdated(res.data);
            setSelectedDeviceId(null);
            setExpandedRecordKey(null);
            setLineResults({});
            setSummaryNote('');
        } catch (e: unknown) {
            onError(e instanceof Error ? e.message : 'Could not save device record.');
        } finally {
            setSaving(false);
        }
    }

    function onSelectDevice(deviceId: string | null) {
        setSelectedDeviceId(deviceId);
        setExpandedRecordKey(null);
        const next: Record<string, 'pass' | 'fail' | 'n/a' | null> = {};
        for (const item of inspectableItems) next[item.itemId] = null;
        setLineResults(next);
        setSummaryNote('');
    }

    function onToggleRecord(record: FieldDeviceRecord) {
        const key = recordKey(record);
        setExpandedRecordKey((current) => (current === key ? null : key));
        setSelectedDeviceId(null);
        setLineResults({});
        setSummaryNote('');
    }

    const expandedRecord = useMemo(
        () => sectionRecords.find((r) => recordKey(r) === expandedRecordKey) || null,
        [sectionRecords, expandedRecordKey]
    );

    return (
        <div className="ir-field-devices">
            <div className="ir-field-devices__intro">
                <h3 className="ir-field-devices__title">Inspect devices one at a time</h3>
                <p className="ir-field-devices__hint">
                    Only <strong>{allowedTypesLabel}</strong> registered for this building appear here.
                    {sectionPlacement ? (
                        <>
                            {' '}
                            Showing <strong>{formatDevicePlacementLabel(sectionPlacement)}</strong> devices (tag each
                            device when registering if the list looks wrong).
                        </>
                    ) : null}
                    {' '}
                    Search, select one device, answer each checklist line for that device, then save to the log below.
                    {requiredDevices.length === 0
                        ? ' Register matching devices under Building Devices first.'
                        : pendingDevices.length > 0
                          ? ` ${pendingDevices.length} of ${requiredDevices.length} device(s) still to inspect.`
                          : ` All ${requiredDevices.length} device(s) logged.`}
                </p>
            </div>

            <ChecklistDevicePicker
                categoryLabel={sectionTitle}
                itemLabel={sectionTitle}
                devices={devices}
                valueId={selectedDeviceId}
                onCommit={onSelectDevice}
                saving={saving}
                disabledReason={
                    disabledReason ||
                    (allowedTypes.length === 0
                        ? 'This section has no device-type filter configured.'
                        : devices.length === 0
                          ? 'No devices registered — add them under the Building Devices tab first.'
                          : pendingDevices.length === 0
                            ? `All ${allowedTypesLabel} devices for this section are already logged.`
                            : null)
                }
                allowedTypes={allowedTypes}
                excludeDeviceIds={loggedDeviceIds}
                triggerLabel="Select device to inspect"
            />

            {selectedDeviceId ? (
                <div className="ir-field-devices__active">
                    <p className="ir-field-devices__active-label">Checklist for this device</p>
                    {inspectableItems.map((item) => (
                        <div key={item.itemId} className="ir-field-devices__line">
                            <p className="ir-field-devices__line-label">{item.label}</p>
                            <div className="ir-result-row" role="radiogroup" aria-label={item.label}>
                                {(['pass', 'fail', 'n/a'] as const).map((r) => {
                                    const active = lineResults[item.itemId] === r;
                                    return (
                                        <button
                                            key={r}
                                            type="button"
                                            role="radio"
                                            aria-checked={active}
                                            onClick={() =>
                                                setLineResults((prev) => ({ ...prev, [item.itemId]: r }))
                                            }
                                            className={[
                                                'ir-result-btn',
                                                `ir-result-btn--${r === 'n/a' ? 'na' : r}`,
                                                active ? 'ir-result-btn--active' : ''
                                            ].filter(Boolean).join(' ')}
                                        >
                                            {r === 'pass' && <span className="ir-result-btn__icon">✓</span>}
                                            {r === 'fail' && <span className="ir-result-btn__icon">✗</span>}
                                            {r === 'n/a' && <span className="ir-result-btn__icon">—</span>}
                                            <span>{r === 'n/a' ? 'N/A' : r.charAt(0).toUpperCase() + r.slice(1)}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                    <textarea
                        className="ir-notes"
                        rows={2}
                        placeholder="Location detail, expiry, model, or other notes for this device…"
                        value={summaryNote}
                        onChange={(e) => setSummaryNote(e.target.value)}
                    />
                    <button
                        type="button"
                        className="ir-field-devices__save"
                        disabled={!allLinesAnswered || saving}
                        onClick={() => void saveDeviceRecord()}
                    >
                        {saving ? 'Saving…' : `Save device (${computedOverall.toUpperCase()})`}
                    </button>
                </div>
            ) : null}

            <div className="ir-field-devices__log">
                <div className="ir-field-devices__log-head">
                    <h3 className="ir-field-devices__log-title">Devices completed in this section</h3>
                    <span className="ir-field-devices__log-count">{sectionRecords.length} logged</span>
                </div>
                {sectionRecords.length === 0 ? (
                    <p className="ir-field-devices__empty">No devices logged yet. Select a device above to begin.</p>
                ) : (
                    <>
                        <p className="ir-field-devices__table-hint">Tap a row to view that device&apos;s inspection summary.</p>
                        <div className="ir-field-devices__table-wrap">
                            <table className="ir-field-devices__table">
                                <thead>
                                    <tr>
                                        <th>Device</th>
                                        <th>Location</th>
                                        <th>Result</th>
                                        <th>Note</th>
                                        <th>Completed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sectionRecords.map((record) => {
                                        const key = recordKey(record);
                                        const isExpanded = expandedRecordKey === key;
                                        return (
                                            <tr
                                                key={key}
                                                className={[
                                                    'ir-field-devices__row',
                                                    'ir-field-devices__row--clickable',
                                                    `ir-field-devices__row--${record.overallResult}`,
                                                    isExpanded ? 'ir-field-devices__row--expanded' : ''
                                                ].join(' ')}
                                                onClick={() => onToggleRecord(record)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        onToggleRecord(record);
                                                    }
                                                }}
                                                tabIndex={0}
                                                role="button"
                                                aria-expanded={isExpanded}
                                            >
                                                <td>{recordTypeLabel(record)}</td>
                                                <td>{recordLocation(record)}</td>
                                                <td>
                                                    <span
                                                        className={`ir-field-devices__result ir-field-devices__result--${record.overallResult === 'n/a' ? 'na' : record.overallResult}`}
                                                    >
                                                        {record.overallResult === 'n/a'
                                                            ? 'N/A'
                                                            : record.overallResult.charAt(0).toUpperCase() + record.overallResult.slice(1)}
                                                    </span>
                                                </td>
                                                <td>{record.note?.trim() || '—'}</td>
                                                <td>
                                                    {record.completedAt ? new Date(record.completedAt).toLocaleString() : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {expandedRecord ? (
                            <FieldDeviceRecordDetail
                                record={recordDetailFromLog(expandedRecord)}
                                onClose={() => setExpandedRecordKey(null)}
                            />
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
}
